import { CheckCircleIcon, ChevronRightIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import type { RootState } from '../../../store/store';
import { isCompanyBrandingComplete } from '../../../utils/branding';
import classNames from '../../../utils/classNames';
import { canAccessSetup, ROUTES } from '../../../utils/routes';
import { useSetupProgress } from '../../../utils/setup';
import { getUserFonctionTypesForCompany } from '../../../utils/users';

interface HeaderProps {
  isAdmin?: boolean;
  isRolesPreviewEnabled?: boolean;
  onToggleRolesPreview?: () => void;
  onOpenRolesPopup?: () => void;
}

function ProgressRing({ percent, size = 56, strokeWidth = 4 }: { percent: number; size?: number; strokeWidth?: number }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - ((percent / 100) * circumference);

  return (
    <svg width={size} height={size} className="shrink-0 -rotate-90">
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="currentColor" strokeWidth={strokeWidth} className="text-rgpd-blue/20" />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        className="text-rgpd-blue transition-all duration-700 ease-out"
      />
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dominantBaseline="central"
        className="rotate-90 fill-rgpd-blue-dark text-xs font-bold"
        style={{ transformOrigin: 'center' }}
      >
        {percent}%
      </text>
    </svg>
  );
}

function StepChip({ done, label, index }: { done: boolean; label: string; index: number }) {
  return (
    <div className={classNames(
      'flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors',
      done ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-rgpd-blue',
    )}>
      {done ? (
        <CheckCircleIcon className="h-3.5 w-3.5" />
      ) : (
        <span className="flex h-4 w-4 items-center justify-center rounded-full bg-rgpd-blue/15 text-[10px] font-bold text-rgpd-blue">{index}</span>
      )}
      <span className={done ? 'line-through' : ''}>{label}</span>
    </div>
  );
}

function Header({ isAdmin = false, isRolesPreviewEnabled = false, onToggleRolesPreview, onOpenRolesPopup }: HeaderProps) {
  const company = useSelector((state: RootState) => state.auth.company);
  const user = useSelector((state: RootState) => state.auth.user);
  const isBrandingComplete = useMemo(() => isCompanyBrandingComplete(company), [company]);
  const { percent, completed, total } = useSetupProgress(company, isBrandingComplete);
  const isComplete = percent === 100;

  // Only users who can reach the setup pages should see the setup progress card,
  // otherwise it would link to a page that returns 403 for them.
  const canAccessSetupPages = useMemo(
    () => canAccessSetup(getUserFonctionTypesForCompany(user, company?.id), isAdmin),
    [user, company?.id, isAdmin],
  );

  const steps = useMemo(() => [
    {
      label: 'Copropriétés',
      done: !!company?.setupSteps?.coproValidation && !!company?.setupSteps?.addMissingCopros && !!company?.setupSteps?.addressValidation,
    },
    { label: 'Activité', done: !!company?.setupSteps?.transactionQualification },
    { label: 'Image de marque & Rapports', done: isBrandingComplete },
    {
      label: 'Collaborateurs',
      done: !!company?.setupSteps?.collaboratorAttribution && !!company?.setupSteps?.mailAlertSetup,
    },
    { label: 'Mails', done: !!company?.setupSteps?.mailConfigSetup },
  ], [company, isBrandingComplete]);

  return (
    <div className="flex flex-col gap-6">
      {/* Admin tools row */}
      {isAdmin && (
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-xs font-semibold text-gray-700 shadow-sm transition-all duration-200 hover:bg-gray-50 hover:shadow-md"
            onClick={onOpenRolesPopup}
          >
            Définir les rôles
          </button>
          <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 shadow-sm">
            <span className="text-xs text-gray-600">Afficher la page avec les rôles</span>
            <button
              type="button"
              role="switch"
              aria-checked={isRolesPreviewEnabled}
              onClick={onToggleRolesPreview}
              className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors duration-200 ${isRolesPreviewEnabled ? 'bg-rgpd-blue' : 'bg-gray-300'}`}
            >
              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${isRolesPreviewEnabled ? 'translate-x-5' : 'translate-x-1'}`} />
            </button>
          </div>
        </div>
      )}

      {/* Setup card */}
      {!company?.id || !canAccessSetupPages ? null : isComplete ? (
        <div className="flex justify-end">
          <Link
            to={ROUTES.SETUP_SIMPLIFIED_DASHBOARD}
            className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-1.5 text-sm font-medium text-emerald-700 shadow-sm transition-colors hover:bg-gray-50"
          >
            <CheckCircleIcon className="h-4 w-4 text-emerald-500" />
            Paramétrage complété
          </Link>
        </div>
      ) : (
        <Link
          to={ROUTES.SETUP_SIMPLIFIED_DASHBOARD}
          className="group relative block overflow-hidden rounded-xl bg-white shadow-md transition-all duration-200 hover:shadow-lg"
        >
          <div className="absolute top-0 right-0 left-0 h-1 rounded-t-xl bg-gradient-to-r from-amber-400 to-amber-500" />

          <div className="flex items-center gap-5 px-6 py-5">
            <ProgressRing percent={percent} />

            <div className="min-w-0 flex-1">
              <div className="mb-1 flex items-center gap-2">
                <Cog6ToothIcon className="h-5 w-5 text-amber-500 transition-transform duration-500 group-hover:rotate-90" />
                <h2 className="text-sm font-bold tracking-wide text-slate-800 uppercase">
                  Action requise : paramétrage préalable de votre compte
                </h2>
                <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-bold text-amber-700">
                  {completed}/{total}
                </span>
              </div>
              <p className="text-sm text-slate-500">
                Complétez ces étapes pour débloquer l'ensemble des fonctionnalités.
              </p>

              <div className="mt-3 hidden flex-wrap gap-2 sm:flex">
                {steps.map((step, i) => (
                  <StepChip key={step.label} done={step.done} label={step.label} index={i + 1} />
                ))}
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-2 rounded-lg bg-amber-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-200 group-hover:bg-amber-600">
              Configurer maintenant
              <ChevronRightIcon className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
            </div>
          </div>

          <div className="h-1 bg-slate-100">
            <div
              className="h-full bg-amber-400 transition-all duration-700 ease-out"
              style={{ width: `${percent}%` }}
            />
          </div>
        </Link>
      )}

    </div>
  );
}

export default Header;
